import { useState, useEffect } from "react"
import DashboardLayout from "../Layout/DashboardLayout"
import { Plus, Edit, Trash, X, Palette, Ruler, Shapes } from "lucide-react"
import axios from "axios";
import {BASE_URL} from '../apiConfig';
import { toast } from 'sonner';
import Cookies from 'js-cookie';
import { useTranslation } from '../LanguageContext';

export default function Page() {
  const [variants, setVariants] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [newVariant, setNewVariant] = useState({
    type: "",
    name: "",
    value: ""
  })
  const [editingVariant, setEditingVariant] = useState({
    id: null,
    type: "",
    name: "",
    value: ""
  })
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [variantToDelete, setVariantToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const token =  Cookies.get('WEB_SSR');
  const { t } = useTranslation()

  // Fetch variants from API
  const fetchVariants = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/variants`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
      if (response.data.success) {
        setVariants(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching variants:', error)
      toast.error(t('variants.fetch.error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVariants()
  }, [])

  // Open edit modal with variant data
  const openEditModal = (variant) => {
    setEditingVariant({
      id: variant.id,
      type: variant.type,
      name: variant.name,
      value: variant.value
    })
    setIsEditModalOpen(true)
  }

  // Handle type change for add modal
  const handleTypeChange = (type) => {
    setNewVariant({
      type: type,
      name: "",
      value: type === "size" ? "" : "#000000"
    })
  }

  // Handle type change for edit modal
  const handleEditTypeChange = (type) => {
    setEditingVariant(prev => ({
      ...prev,
      type: type,
      value: type === "size" ? prev.name : (prev.value || "#000000")
    }))
  }

  // Add new variant
  const handleAddVariant = async () => {
    if (!newVariant.type || !newVariant.name || !newVariant.value) {
      toast.error(t('variants.fill.fields'))
      return
    }

    setActionLoading(true)
    try {
      const response = await axios.post(`${BASE_URL}/api/variants`, newVariant, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
      
      if (response.data.success) {
        fetchVariants()
        setNewVariant({ type: "", name: "", value: "" })
        setIsModalOpen(false)
        toast.success(t('variants.add.success'))
      } else {
        toast.error(response.data.message || t('variants.add.error'))
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat()
        toast.error(errors.join('\n'))
      } else {
        toast.error(error.response?.data?.message || t('variants.add.error'))
      }
    } finally {
      setActionLoading(false)
    }
  }

  // Update existing variant
  const handleUpdateVariant = async () => {
    if (!editingVariant.type || !editingVariant.name || !editingVariant.value) {
      toast.error(t('variants.fill.fields'))
      return
    }

    setActionLoading(true)
    try {
      const response = await axios.put(`${BASE_URL}/api/variants/${editingVariant.id}`, editingVariant, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
      
      if (response.data.success) {
        fetchVariants()
        setIsEditModalOpen(false)
        setEditingVariant({ id: null, type: "", name: "", value: "" })
        toast.success(t('variants.update.success'))
      } else {
        toast.error(response.data.message || t('variants.update.error'))
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat()
        toast.error(errors.join('\n'))
      } else {
        toast.error(error.response?.data?.message || t('variants.update.error'))
      }
    } finally {
      setActionLoading(false)
    }
  }

  // Show delete confirmation modal
  const confirmDeleteVariant = (variant) => {
    setVariantToDelete(variant)
    setShowDeleteModal(true)
  }

  // Handle delete variant
  const handleDeleteVariant = async () => {
    if (!variantToDelete) return
    
    setIsDeleting(true)
    try {
      const response = await axios.delete(`${BASE_URL}/api/variants/${variantToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (response.data.success) {
        fetchVariants()
        toast.success(t('variants.delete.success'))
        setShowDeleteModal(false)
        setVariantToDelete(null)
      } else {
        toast.error(response.data.message || t('variants.delete.error'))
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('variants.delete.error'))
    } finally {
      setIsDeleting(false)
    }
  }

  // Reset add form
  const resetAddForm = () => {
    setNewVariant({ type: "", name: "", value: "" })
    setIsModalOpen(false)
  }

  // Reset edit form
  const resetEditForm = () => {
    setEditingVariant({ id: null, type: "", name: "", value: "" })
    setIsEditModalOpen(false)
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-8 flex justify-between">
          <div>
            <h1 className="text-3xl max-lg:text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">{t('variants.title')}</h1>
            <p className="text-gray-600 text-xs dark:text-gray-400">
              {t('variants.subtitle')}
            </p>
          </div>
          <div>
            <button 
              onClick={() => setIsModalOpen(true)}
              disabled={loading || actionLoading}
              className="bg-[#171717] text-nowrap max-lg:gap-0 text-white rounded-lg text-xs py-2 px-3 flex items-center gap-2 shadow-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 text-white" />  
              {t('variants.add')}
            </button>
          </div>
        </div>

        {loading ? (
          <>
            {/* Skeleton Loading State */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                      {[t('variants.table.name'), t('variants.table.value'), t('variants.table.type'), t('variants.table.actions')].map((_, i) => (
                        <th key={i} className="px-6 py-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {/* Skeleton rows - 5 rows */}
                    {[...Array(5)].map((_, rowIndex) => (
                      <tr key={rowIndex} className="animate-pulse">
                        {/* Name column skeleton */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600"></div>
                            <div className="space-y-2">
                              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                            </div>
                          </div>
                        </td>
                        
                        {/* Value column skeleton */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                          </div>
                        </td>
                        
                        {/* Type column skeleton */}
                        <td className="px-6 py-4">
                          <div className="inline-flex">
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16"></div>
                          </div>
                        </td>
                        
                        {/* Actions column skeleton */}
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <div className="h-7 w-7 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-7 w-7 bg-gray-200 dark:bg-gray-700 rounded"></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Skeleton */}
            <div className="mt-6 flex items-center justify-between animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
              <div className="flex gap-2">
                <div className="h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                <div className="h-9 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              </div>
            </div>
          </>
        ) : (
          <>
            {variants.length > 0 ? (
              <>
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                          <th className="px-6 py-2 text-left font-semibold text-gray-900 dark:text-gray-50">{t('variants.table.name')}</th>
                          <th className="px-6 py-2 text-left font-semibold text-gray-900 dark:text-gray-50">{t('variants.table.value')}</th>
                          <th className="px-6 py-2 text-left font-semibold text-gray-900 dark:text-gray-50">{t('variants.table.type')}</th>
                          <th className="px-6 py-2 text-left font-semibold text-gray-900 dark:text-gray-50">{t('variants.table.actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                        {variants.map((variant) => (
                          <tr
                            key={variant.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150"
                          >
                            <td className="px-6 py-3">
                              <div className="flex items-center gap-2">
                                {variant.type === "color" ? (
                                  <>
                                    <div
                                      className='w-6 h-6 rounded border border-gray-300 dark:border-gray-600'
                                      style={{ backgroundColor: variant.value }}
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">{variant.name}</span>
                                  </>
                                ) : (
                                  <span className="text-gray-700 dark:text-gray-300">{variant.name}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-3">
                              {variant.type === "color" ? (
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-4 h-4 rounded border border-gray-300"
                                    style={{ backgroundColor: variant.value }}
                                  />
                                  <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                                    {variant.value}
                                  </code>
                                </div>
                              ) : (
                                <span className="bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200 px-3 py-1 rounded-full text-xs font-medium">
                                  {variant.value}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                variant.type === "color" 
                                  ? "bg-purple-50 dark:bg-purple-900 text-purple-700 dark:text-purple-200"
                                  : "bg-green-50 dark:bg-green-900 text-green-700 dark:text-green-200"
                              }`}>
                                {variant.type}
                              </span>
                            </td>
                            <td className="px-6 py-3 text-nowrap space-x-2">
                              <button 
                                onClick={() => openEditModal(variant)}
                                disabled={actionLoading}
                                className="inline-flex items-center justify-center p-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50"
                                title={t('variants.modal.edit.title')}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => confirmDeleteVariant(variant)}
                                disabled={actionLoading}
                                className="inline-flex items-center justify-center p-1.5 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                title={t('variants.modal.delete.title')}
                              >
                                <Trash className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {t('variants.pagination.showing')} {variants.length} {t('variants.pagination.of')} {variants.length}
                  </p>
                  <div className="flex gap-2">
                    <button className="px-4 py-2 border border-gray-200 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium text-xs">
                      {t('variants.pagination.previous')}
                    </button>
                    <button className="px-4 py-2 text-white rounded-lg bg-[#171717] shadow-md transition-colors font-medium text-xs">
                      {t('variants.pagination.next')}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Shapes className="w-20 h-20 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">{t('variants.empty.title')}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  {t('variants.empty.desc')}
                </p>
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-[#171717] text-white rounded-lg text-xs py-2 px-3 flex items-center gap-1 shadow-md hover:bg-gray-800 transition-colors"
                >
                  <Plus className="w-4 h-4 text-white" />  
                  {t('variants.add')}
                </button>
              </div>
            )}
          </>
        )}

        {/* Add Variant Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-md border border-gray-200 dark:border-gray-800 shadow-xl">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('variants.modal.add.title')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t('variants.modal.add.desc')}
                  </p>
                </div>
                <button
                  onClick={resetAddForm}
                  disabled={actionLoading}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Variant Type Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('variants.modal.select_type')}
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleTypeChange("color")}
                      disabled={actionLoading}
                      className={`flex-1 flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                        newVariant.type === "color"
                          ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                          : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Palette className={`w-6 h-6 mb-2 ${
                        newVariant.type === "color" 
                          ? "text-purple-600 dark:text-purple-400" 
                          : "text-gray-500 dark:text-gray-400"
                      }`} />
                      <span className={`font-medium ${
                        newVariant.type === "color"
                          ? "text-purple-700 dark:text-purple-300"
                          : "text-gray-700 dark:text-gray-300"
                      }`}>
                        {t('variants.modal.color')}
                      </span>
                    </button>
                    
                    <button
                      onClick={() => handleTypeChange("size")}
                      disabled={actionLoading}
                      className={`flex-1 flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                        newVariant.type === "size"
                          ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                          : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Ruler className={`w-6 h-6 mb-2 ${
                        newVariant.type === "size" 
                          ? "text-green-600 dark:text-green-400" 
                          : "text-gray-500 dark:text-gray-400"
                      }`} />
                      <span className={`font-medium ${
                        newVariant.type === "size"
                          ? "text-green-700 dark:text-green-300"
                          : "text-gray-700 dark:text-gray-300"
                      }`}>
                        {t('variants.modal.size')}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Color Form */}
                {newVariant.type === "color" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('variants.modal.color_name')}
                      </label>
                      <input
                        type="text"
                        value={newVariant.name}
                        onChange={(e) => setNewVariant({...newVariant, name: e.target.value})}
                        disabled={actionLoading}
                        placeholder="e.g., Red, Blue, Black"
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600 focus:border-transparent disabled:opacity-50"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('variants.modal.hex')}
                        </label>
                        {newVariant.value && (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded border border-gray-300"
                              style={{ backgroundColor: newVariant.value }}
                            />
                            <span className="text-xs text-gray-500">{newVariant.value}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={newVariant.value}
                          onChange={(e) => setNewVariant({...newVariant, value: e.target.value})}
                          disabled={actionLoading}
                          placeholder="#000000"
                          className="flex-1 px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600 focus:border-transparent disabled:opacity-50"
                        />
                        <input
                          type="color"
                          value={newVariant.value}
                          onChange={(e) => setNewVariant({...newVariant, value: e.target.value})}
                          disabled={actionLoading}
                          className="w-12 h-12 p-1 border border-gray-300 dark:border-gray-700 rounded-lg cursor-pointer disabled:opacity-50"
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('variants.modal.hex_or_picker')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Size Form */}
                {newVariant.type === "size" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('variants.modal.size_label')}
                      </label>
                      <input
                        type="text"
                        value={newVariant.name}
                        onChange={(e) => setNewVariant({
                          type: "size",
                          name: e.target.value,
                          value: e.target.value
                        })}
                        disabled={actionLoading}
                        placeholder="e.g., XL, L, M, S"
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600 focus:border-transparent disabled:opacity-50"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('variants.modal.size_desc')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-3">
                <button
                  onClick={resetAddForm}
                  disabled={actionLoading}
                  className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-300 dark:border-gray-700 disabled:opacity-50"
                >
                  {t('variants.modal.cancel')}
                </button>
                <button
                  onClick={handleAddVariant}
                  disabled={!newVariant.name || !newVariant.value || actionLoading}
                  className={`px-3 py-2 text-xs font-medium text-white rounded-lg transition-colors shadow-sm flex items-center gap-1 ${
                    newVariant.name && newVariant.value && !actionLoading
                      ? "bg-[#171717] hover:bg-gray-800"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {actionLoading ? (
                    <>
                      <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t('variants.modal.adding')}
                    </>
                  ) : (
                    <>
                      <Plus className="w-3.5 h-3.5" />
                      {t('variants.modal.add_variant')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Variant Modal */}
        {isEditModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-md border border-gray-200 dark:border-gray-800 shadow-xl">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('variants.modal.edit.title')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t('variants.modal.edit.desc')}
                  </p>
                </div>
                <button
                  onClick={resetEditForm}
                  disabled={actionLoading}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Variant Type Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('variants.modal.edit.variant_type')}
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleEditTypeChange("color")}
                      disabled={actionLoading}
                      className={`flex-1 flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                        editingVariant.type === "color"
                          ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                          : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Palette className={`w-6 h-6 mb-2 ${
                        editingVariant.type === "color" 
                          ? "text-purple-600 dark:text-purple-400" 
                          : "text-gray-500 dark:text-gray-400"
                      }`} />
                      <span className={`font-medium ${
                        editingVariant.type === "color"
                          ? "text-purple-700 dark:text-purple-300"
                          : "text-gray-700 dark:text-gray-300"
                      }`}>
                        {t('variants.modal.color')}
                      </span>
                    </button>
                    
                    <button
                      onClick={() => handleEditTypeChange("size")}
                      disabled={actionLoading}
                      className={`flex-1 flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                        editingVariant.type === "size"
                          ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                          : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Ruler className={`w-6 h-6 mb-2 ${
                        editingVariant.type === "size" 
                          ? "text-green-600 dark:text-green-400" 
                          : "text-gray-500 dark:text-gray-400"
                      }`} />
                      <span className={`font-medium ${
                        editingVariant.type === "size"
                          ? "text-green-700 dark:text-green-300"
                          : "text-gray-700 dark:text-gray-300"
                      }`}>
                        {t('variants.modal.size')}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Color Form */}
                {editingVariant.type === "color" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('variants.modal.color_name')}
                      </label>
                      <input
                        type="text"
                        value={editingVariant.name}
                        onChange={(e) => setEditingVariant({...editingVariant, name: e.target.value})}
                        disabled={actionLoading}
                        placeholder="e.g., Red, Blue, Black"
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600 focus:border-transparent disabled:opacity-50"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('variants.modal.hex')}
                        </label>
                        {editingVariant.value && (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded border border-gray-300"
                              style={{ backgroundColor: editingVariant.value }}
                            />
                            <span className="text-xs text-gray-500">{editingVariant.value}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={editingVariant.value}
                          onChange={(e) => setEditingVariant({...editingVariant, value: e.target.value})}
                          disabled={actionLoading}
                          placeholder="#000000"
                          className="flex-1 px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600 focus:border-transparent disabled:opacity-50"
                        />
                        <input
                          type="color"
                          value={editingVariant.value}
                          onChange={(e) => setEditingVariant({...editingVariant, value: e.target.value})}
                          disabled={actionLoading}
                          className="w-12 h-12 p-1 border border-gray-300 dark:border-gray-700 rounded-lg cursor-pointer disabled:opacity-50"
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('variants.modal.hex_or_picker')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Size Form */}
                {editingVariant.type === "size" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('variants.modal.size_label')}
                      </label>
                      <input
                        type="text"
                        value={editingVariant.name}
                        onChange={(e) => setEditingVariant({
                          ...editingVariant,
                          type: "size",
                          name: e.target.value,
                          value: e.target.value
                        })}
                        disabled={actionLoading}
                        placeholder="e.g., XL, L, M, S"
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600 focus:border-transparent disabled:opacity-50"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('variants.modal.size_desc')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-3">
                <button
                  onClick={resetEditForm}
                  disabled={actionLoading}
                  className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-300 dark:border-gray-700 disabled:opacity-50"
                >
                  {t('variants.modal.cancel')}
                </button>
                <button
                  onClick={handleUpdateVariant}
                  disabled={!editingVariant.name || !editingVariant.value || actionLoading}
                  className={`px-3 py-2 text-xs font-medium text-white rounded-lg transition-colors shadow-sm flex items-center gap-1 ${
                    editingVariant.name && editingVariant.value && !actionLoading
                      ? "bg-[#171717] hover:bg-gray-800"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {actionLoading ? (
                    <>
                      <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t('variants.modal.edit.updating')}
                    </>
                  ) : (
                    <>
                      <Edit className="w-3.5 h-3.5" />
                      {t('variants.modal.edit.update')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && variantToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('variants.modal.delete.title')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('variants.modal.delete.warning')}</p>
                </div>
              </div>
              
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-gray-900 dark:text-gray-50">
                  {t('variants.modal.delete.confirm')} <span className="font-semibold">"{variantToDelete.name}"</span> {t('variants.modal.delete.variant_name')}
                </p>
                {variantToDelete.type === "color" && (
                  <div className="flex items-center gap-2 mt-2">
                    <div 
                      className="w-4 h-4 rounded border border-gray-300"
                      style={{ backgroundColor: variantToDelete.value }}
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{variantToDelete.value}</span>
                  </div>
                )}
                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                  {t('variants.modal.delete.association')}
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setVariantToDelete(null)
                  }}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {t('variants.modal.cancel')}
                </button>
                <button
                  onClick={handleDeleteVariant}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('variants.modal.delete.deleting')}
                    </>
                  ) : (
                    t('variants.modal.delete.confirm_btn')
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
